import { Image } from 'expo-image';
import { router } from 'expo-router';
import { useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { Platform, StyleSheet, View } from 'react-native';
import Animated, { FadeOutLeft, SlideInRight } from 'react-native-reanimated';

import { useClaimNpoSendEmailWithFiles, useClaimNpoValidation } from '@/api/hooks/nonProfitOrgHooks';
import { claimIcon } from '@/assets/images/icons';
import { merchantDocumentsAcceptedFileTypes } from '@/constants/options/MerchantDocumentsAccetedFileTypes';
import { useAuth } from '@/contexts/authContext';
import { useEndFlowModal } from '@/contexts/endFlowModalContext';
import { ClaimNpoData, claimNpoResolver } from '@/lib/validations/ClaimNpoSchema';
import { i18n } from '@/utils/i18n';

import BrandedButton from '../buttons/BrandedButton';
import GradientCircularButton from '../buttons/GradientCircularButton';
import FileInputField from '../inputs/FileInputField';
import PhoneInput from '../inputs/PhoneInput';
import TextInputField from '../inputs/TextInputField';
import ClaimNpoVerifyCodeModal from '../modals/ClaimNpoVerifyCodeModal';
import OnboardingSection from '../npo/onboarding/OnboardingSection';
import Text from '../Text';
import FormWrapper from './FormWrapper';

interface ClaimNpoFormProps {
  nonProfitOrgId: string;
  npoName: string;
  onClaimed?: () => void;
}

const ClaimNpoForm = ({ nonProfitOrgId, npoName, onClaimed }: ClaimNpoFormProps) => {
  const { state: authState } = useAuth();

  const { showSuccess } = useEndFlowModal();

  const initialData = {
    firstName: authState.user?.user.firstName ?? '',
    lastName: authState.user?.user.lastName ?? '',
    npoName: npoName ?? '',
    email: authState.user?.user.email ?? '',
    phone: authState.user?.user.phone ?? ''
  };

  const {
    control: contactControl,
    handleSubmit: handleContactSubmit,
    getValues,
    setFocus,
    setValue,
    trigger,
    formState: { isValid, submitCount, errors },
    resetField,
    setError
  } = useForm<ClaimNpoData>({
    defaultValues: initialData,
    mode: 'onChange',
    resolver: claimNpoResolver
  });

  const { mutateAsync: claimNpoValidation, isPending: sendEmailPending } = useClaimNpoValidation();
  const { mutateAsync: claimNpoWithFiles, isPending: claimingNpoPending } = useClaimNpoSendEmailWithFiles();

  const [hasClaimed, setHasClaimed] = useState(false);
  const [validationModalVisible, setValidationModalVisible] = useState(false);
  const [validationId, setValidationId] = useState<string>('');

  const parseDocumentsData = (
    data: ClaimNpoData
  ): {
    driverLicense: string | File;
    articlesOfIncorporation: string | File;
  } => {
    const parsedDocuments = {
      driverLicense: Platform.OS === 'web' ? (data.driverLicense as File) : (data.driverLicense as string),
      articlesOfIncorporation:
        Platform.OS === 'web' ? (data.articlesOfIncorporation as File) : (data.articlesOfIncorporation as string)
    };

    return parsedDocuments;
  };

  const onClaimNpo: SubmitHandler<ClaimNpoData> = async (data) => {
    claimNpoValidation(
      {
        nonProfitOrgId: nonProfitOrgId,
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        phone: data.phone
      },
      {
        onSuccess: (data) => {
          setValidationId(data.validationId);
          setValidationModalVisible(true);
          onClaimed && onClaimed();
        },
        onError: (e) => {
          console.error(e);
        }
      }
    );
  };

  const onVerifyCode = async ({ validationCode }: { validationCode: string }) => {
    setValidationModalVisible(false);

    const parsedFiles = parseDocumentsData(getValues());
    claimNpoWithFiles(
      {
        body: {
          validationId: validationId,
          validationCode: validationCode
        },
        nonProfitOrgId: nonProfitOrgId,
        driverLicense: parsedFiles.driverLicense,
        articlesOfIncorporation: parsedFiles.articlesOfIncorporation
      },
      {
        onSuccess: () => {
          showSuccess({
            title: i18n.t('claim_npo.claim_success_title'),
            message: i18n.t('claim_npo.claim_success_message')
          });
          setHasClaimed(true);
        }
      }
    );
  };

  if (hasClaimed) {
    return (
      <Animated.View style={styles.mainContainer} entering={SlideInRight} exiting={FadeOutLeft}>
        <Text fontWeight="700" style={styles.boldTitle}>
          {i18n.t('claim_npo.info_submitted')}
        </Text>
        <Text fontWeight="500" style={[styles.labelText, styles.browseMeanwhile]}>
          {i18n.t('claim_npo.browse_bs_meanwhile')}
        </Text>
        <BrandedButton onPress={() => router.replace('/home/npoTabs')}>{i18n.t('mix.browse')}</BrandedButton>
      </Animated.View>
    );
  }

  return (
    <FormWrapper horizontalPadding={0}>
      <ClaimNpoVerifyCodeModal
        visible={validationModalVisible}
        onValidatePressed={(code) => onVerifyCode({ validationCode: code })}
        onCloseModal={() => setValidationModalVisible(false)}
      />
      <Animated.View style={styles.mainContainer} entering={SlideInRight} exiting={FadeOutLeft}>
        <GradientCircularButton style={styles.headerIcon} disabled size={92}>
          <Image style={styles.icon} source={claimIcon} contentFit={'contain'} />
        </GradientCircularButton>
        <Text fontWeight="700" style={[styles.boldTitle, styles.centerText]}>
          {i18n.t('claim_npo.claim_form_title', { NPO_NAME: npoName })}
        </Text>
        <Text fontWeight="500" style={[styles.labelText, styles.centerText]}>
          {i18n.t('claim_npo.claim_form_subtitle')}
        </Text>
        <Text style={[styles.subLabel, styles.centerText]}>{i18n.t('claim_npo.secured_data')}</Text>
        <View style={styles.textFields}>
          <TextInputField
            control={contactControl}
            name={'firstName'}
            label={i18n.t('claim_npo.firstName_label')}
            placeholder={i18n.t('claim_npo.firstName_placeholder')}
            returnKeyLabel="next"
            returnKeyType="next"
            onSubmitEditing={() => setFocus('lastName')}
          />
          <TextInputField
            control={contactControl}
            name={'lastName'}
            label={i18n.t('claim_npo.lastName_label')}
            placeholder={i18n.t('claim_npo.lastName_placeholder')}
            returnKeyLabel="next"
            returnKeyType="next"
            onSubmitEditing={() => setFocus('npoName')}
          />
          <TextInputField
            control={contactControl}
            name={'npoName'}
            label={i18n.t('claim_npo.npoName_label')}
            placeholder={i18n.t('claim_npo.npoName_placeholder')}
            returnKeyLabel="next"
            returnKeyType="next"
            onSubmitEditing={() => setFocus('email')}
          />

          <TextInputField
            control={contactControl}
            name={'email'}
            label={i18n.t('claim_npo.email_label')}
            placeholder={i18n.t('claim_npo.email_placeholder')}
            returnKeyLabel="next"
            returnKeyType="next"
            type="email-address"
            onSubmitEditing={() => setFocus('phone')}
          />

          <View style={styles.phoneInputContainer}>
            <PhoneInput
              initialValue={initialData.phone}
              label={i18n.t('claim_npo.phone_label')}
              placeholder={i18n.t('claim_npo.phone_placeholder')}
              error={errors.phone}
              onNumberChange={(number) => {
                if (number) {
                  const fullNumber = `${number?.getNationalNumber()}`;
                  setValue('phone', fullNumber);
                  trigger('phone');
                } else {
                  resetField('phone');
                  setError('phone', {
                    type: 'required',
                    message: i18n.t('mix.valid_phone_number')
                  });
                }
              }}
            />
          </View>
        </View>

        <OnboardingSection label={i18n.t('claim_npo.driverLicense_label')} gap={10}>
          <FileInputField
            control={contactControl}
            name="driverLicense"
            showError
            acceptedTypes={merchantDocumentsAcceptedFileTypes}
            getBlob
            maxFileSize={15}
            allowCamera
          />
        </OnboardingSection>
        <OnboardingSection label={i18n.t('claim_npo.articlesOfIncorporation_label')} gap={10}>
          <FileInputField
            control={contactControl}
            name="articlesOfIncorporation"
            acceptedTypes={merchantDocumentsAcceptedFileTypes}
            getBlob
            maxFileSize={15}
            allowCamera
          />
        </OnboardingSection>

        <BrandedButton
          loading={sendEmailPending || claimingNpoPending}
          style={styles.button}
          backGroundColor={!isValid && submitCount > 0 ? 'gray' : '#FE9E22'}
          disabled={!isValid && submitCount > 0}
          onPress={handleContactSubmit(onClaimNpo)}>
          {i18n.t('claim_npo.claim_button_label', { NPO_NAME: npoName })}
        </BrandedButton>
      </Animated.View>
    </FormWrapper>
  );
};

export default ClaimNpoForm;

const styles = StyleSheet.create({
  mainContainer: {
    flex: 1,
    flexDirection: 'column',
    marginBottom: 16,
    gap: 25
  },

  boldTitle: {
    fontSize: 22,
    color: 'black'
  },

  labelText: {
    fontSize: 18
  },

  subLabel: {
    fontSize: 16,
    color: '#0F766E'
  },

  browseMeanwhile: {
    marginTop: 15,
    marginBottom: 25
  },

  textFields: {
    gap: 20,
    marginBottom: 25
  },

  centerText: {
    textAlign: 'center',
    alignSelf: 'center'
  },

  headerIcon: {
    alignSelf: 'center'
  },

  icon: {
    width: 48,
    height: 48
  },

  phoneInputContainer: {
    zIndex: 10
  },

  button: {
    width: '100%'
  }
});
